import React, { Fragment, useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid2 from "@mui/material/Grid2";
import CircularProgress from "@mui/material/CircularProgress";
import {
    uploadDeviceFOTAFirmware,
    uploadDeviceFactoryFirmwareWithAutoSign,
    getDeviceFOTAFirmwareData,
    downloadDeviceFotaFirmwareFile,
    getDeviceModelNumbersList,
    intiFOTAFirmwareStatusInfo,
} from "../../../action/firmware";
import { LabelText } from "../../Common/Typography/AppTypography";
import { setAlert } from "../../../action/alert";
import CloudDownload from "@mui/icons-material/CloudDownload";
import customConfig from "../../../config";
import { selectMenuProps } from "../../../utils/selectMenuProps";
import FormInput from "../../Common/FormInputs/MuiTextField";
import AppCheckbox from "../../Common/FormInputs/AppCheckbox";
import AppRadioGroup from "../../Common/FormInputs/AppRadioGroup";
import AppButton from "../../Common/Button/Button";
import DataTable from "../../Common/DataTable/DataTable";
import AppTabs from "../../Common/Tabs/AppTabs";
import { useTheme } from "@mui/material/styles";
import { useDesignTokens } from "../../../theme/useDesignTokens";

const formControlSx = (theme) => ({ width: "100%", maxWidth: "100%", [theme.breakpoints.up("md")]: { maxWidth: 280 } });
const TABLE_HEADERS = {
    firmwareVersion: "Firmware Version",
    deviceCount: "Device Count",
    firmwareImage: "Firmware Image",
    firmwareSign: "Firmware Signature",
};

function getFOTAFileName(categoryName) {
    categoryName = categoryName.toUpperCase()

    if (categoryName==="AC" || categoryName==="CAC")
        return "ac"
    else if (categoryName==="FANCONTROLLER" || categoryName==="ANCHORFAN")
        return "fan"
    else if (categoryName==="REFRIGERATOR")
        return "ref"
    else if (categoryName==="SWITCH" || categoryName==="SWITCHES" || categoryName==="PLUG")
        return "asw"
    else if (categoryName==="ODMFRONTLOADWM")
        return "wm"
    else if (categoryName==="ROMASWITCHES")
        return "rsd"
    else if (categoryName==="WATERHEATER")
        return "wh"
    else if (categoryName==="TOPLOADWM")
        return "tlwm"
    else if (categoryName==="LEDLIGHTS")
        return "lights"
    else if (categoryName==="ODMIRB")
        return "irb"
    else
        return categoryName
}

const DeviceFOTAFirmware = ({
    allowedDeviceCategories,
    allowedCompanyBrand,
    firmwareData,
    uploadDeviceFOTAFirmware,
    uploadDeviceFactoryFirmwareWithAutoSign,
    downloadDeviceFotaFirmwareFile,
    intiFOTAFirmwareStatusInfo,
    setAlert
}) => {
    const [activeTab, setActiveTab] = useState(0);
    const FOTA_TABS = [{ label: "View Firmware Info" }, { label: "Upload Firmware" }];

    return (
        <Fragment>
            <Box sx={{ width: "100%", minWidth: 0 }} className="device-firmware-nested-tabs">
                <AppTabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    tabs={FOTA_TABS}
                    variant="scrollable"
                    scrollButtons="auto"
                    tabMinWidth="auto"
                    appearance="neutral"
                    sx={{ mt: 2 }}
                />
                {activeTab === 0 && (
                    <ViewFirmwareInfo
                        allowedDeviceCategories={allowedDeviceCategories}
                        allowedCompanyBrand={allowedCompanyBrand}
                        downloadDeviceFotaFirmwareFile={downloadDeviceFotaFirmwareFile}
                        setAlert={setAlert}
                    />
                )}
                {activeTab === 1 && (
                    <UploadFirmware
                        allowedDeviceCategories={allowedDeviceCategories}
                        allowedCompanyBrand={allowedCompanyBrand}
                        firmwareData={firmwareData}
                        uploadDeviceFOTAFirmware={uploadDeviceFOTAFirmware}
                        uploadDeviceFactoryFirmwareWithAutoSign={uploadDeviceFactoryFirmwareWithAutoSign}
                        intiFOTAFirmwareStatusInfo={intiFOTAFirmwareStatusInfo}
                        setAlert={setAlert}
                    />
                )}
            </Box>
        </Fragment>
    );
}

const ViewFirmwareInfo = ({
    allowedDeviceCategories,
    allowedCompanyBrand,
    downloadDeviceFotaFirmwareFile,
    setAlert,
}) => {
    const theme = useTheme();
    const { colors } = useDesignTokens();
    const [categoryFilter, setCategoryFilter] = useState("");
    const [brandFilter, setBrandFilter] = useState("");
    const [modelNumberFilterList, setModelNumberFilterList] = useState([]);
    const [modelNumberFilter, setModelNumberFilter] = useState("");
    const [deviceFirmwareData, setDeviceFirmwareData] = useState([]);
    const [showFirmwareDataList, setShowFirmwareDataList] = useState(false);
    const [isDeviceFirmwareDataLoading, setIsDeviceFirmwareDataLoading] = useState(false);
    const [updateFlag, setUpdateFlag] = useState(undefined);
    const [isFilterDisabled, setIsFilterDisabled] = useState(false);

    useEffect(() => {
        if (!categoryFilter || categoryFilter.length===0) {
            return
        }
        if (!brandFilter || brandFilter.length===0) {
            return
        }

        if (categoryFilter && categoryFilter.length!==0 && brandFilter && brandFilter.length!==0) {
            getDeviceModelNumbersList(brandFilter, categoryFilter)
                .then((data) => {
                    setModelNumberFilterList(data)
                    setModelNumberFilter("")
                    setShowFirmwareDataList(false)
                })
                .catch((err) => {
                    setModelNumberFilterList([])
                    setShowFirmwareDataList(false)
                })
        }
    }, [categoryFilter, brandFilter])

    useEffect(() => {
        setShowFirmwareDataList(false)
    }, [categoryFilter, brandFilter, modelNumberFilter])

    useEffect(() => {
        if (!categoryFilter || categoryFilter.length===0) {
            return
        }
        if (!brandFilter || brandFilter.length===0) {
            return
        }

        getDevicesFirmwareData();
    }, [updateFlag]);

    function getDevicesFirmwareData() {
        setIsDeviceFirmwareDataLoading(true);

        let filteredParams = {}
        if (categoryFilter && categoryFilter.length!==0) {
            filteredParams['categoryName'] = categoryFilter
        }

        if (brandFilter && brandFilter.length!==0) {
            filteredParams['brandName'] = brandFilter
        }

        if (modelNumberFilter && modelNumberFilter.length!==0) {
            filteredParams['modelNumber'] = modelNumberFilter
        }

        getDeviceFOTAFirmwareData(filteredParams)
            .then((res) => {
                setIsDeviceFirmwareDataLoading(false);
                setShowFirmwareDataList(true);
                setDeviceFirmwareData(res);
                setIsFilterDisabled(false);
            })
            .catch((err) => {
                setIsDeviceFirmwareDataLoading(false);
                setIsFilterDisabled(false);
                setAlert(err?.response?.data?.message ?? "Failed to Get FOTA Firmware Details", "danger")
            });
    };
    
    function filterDeviceFirmwareData() {
        if (!categoryFilter || categoryFilter.length===0) {
            setAlert("Category Cannot be Empty", "danger")
            return
        }

        if (!brandFilter || brandFilter.length===0) {
            setAlert("Brand Cannot be Empty", "danger")
            return
        }

        setIsFilterDisabled(true);
        setUpdateFlag(new Date().getTime());
    }

    function resetFilters() {
        setCategoryFilter("Select Category");
        setBrandFilter("Select Brand");
        setModelNumberFilterList([]);
        setModelNumberFilter("Select Model Number");
        setDeviceFirmwareData([]);
        setShowFirmwareDataList(false);
        setIsFilterDisabled(false);
        setUpdateFlag(undefined);
    }

    function downloadFile(firmwareVersion, fileType) {
        const params = {};
        if (categoryFilter && categoryFilter.length > 0) params['categoryName'] = categoryFilter;
        if (brandFilter && brandFilter.length > 0) params['brandName'] = brandFilter;
        if (modelNumberFilter && modelNumberFilter.length > 0) params['modelNumber'] = modelNumberFilter;
        if (firmwareVersion) params['version'] = firmwareVersion;  // backend expects 'version', not 'firmwareVersion'

        downloadDeviceFotaFirmwareFile(params, fileType)
            .then(() => {
            })
            .catch((err) => {
                setAlert(err?.response?.data?.message ?? "Failed to Download File", "danger")
            })
    }
    

    // if (isDeviceFirmwareDataLoading) {
    //     return <div style={{ display: "grid", placeItems: "center", height: "20vh" }}>
    //         <CircularProgress size="5em" style={{ color: "#AFB42B" }} />
    //     </div>
    // }
    
    const buildDownloadLink = (firmwareVersion, fileType) => (
        <Box
            component="span"
            sx={{ cursor: "pointer", textDecoration: "underline", display: "inline-flex", alignItems: "center" }}
            onClick={() => downloadFile(firmwareVersion, fileType)}
        >
            <CloudDownload sx={{ color: "black", fontSize: 20 }} />
        </Box>
    );

    const tableColumns = [
        { key: "firmwareVersion", label: TABLE_HEADERS.firmwareVersion, minWidth: 140 },
        { key: "deviceCount", label: TABLE_HEADERS.deviceCount, minWidth: 120 },
        {
            key: "firmwareImage",
            label: TABLE_HEADERS.firmwareImage,
            minWidth: 120,
            render: (_, row) => buildDownloadLink(row.firmwareVersion, "firmwareImage"),
        },
        {
            key: "firmwareSign",
            label: TABLE_HEADERS.firmwareSign,
            minWidth: 120,
            render: (_, row) => buildDownloadLink(row.firmwareVersion, "firmwareSign"),
        },
    ];

    return (
        <Fragment>
            <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, mb: 2 }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr auto" },
                        gap: 2.5,
                        alignItems: "center",
                        maxWidth: "100%",
                    }}
                >
                    <FormInput
                        type="select"
                        size="small"
                        fullWidth
                        label="Category"
                        name="categoryFilter"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        options={(allowedDeviceCategories || []).map((el) => ({ value: el, label: el }))}
                        MenuProps={selectMenuProps}
                        sx={{ minWidth: 0 }}
                    />
                    <FormInput
                        type="select"
                        size="small"
                        fullWidth
                        label="Brand"
                        name="brandFilter"
                        value={brandFilter}
                        onChange={(e) => setBrandFilter(e.target.value)}
                        options={(allowedCompanyBrand || []).map((el) => ({ value: el, label: el }))}
                        MenuProps={selectMenuProps}
                        sx={{ minWidth: 0 }}
                    />
                    <FormInput
                        type="select"
                        size="small"
                        fullWidth
                        label="Model Number"
                        name="modelNumberFilter"
                        value={modelNumberFilter}
                        onChange={(e) => setModelNumberFilter(e.target.value)}
                        options={(modelNumberFilterList || []).map((el) => ({ value: el, label: el }))}
                        MenuProps={selectMenuProps}
                        sx={{ minWidth: 0 }}
                    />
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: { xs: "flex-start", lg: "flex-end" } }}>
                        <AppButton variant="primary" size="medium" onClick={filterDeviceFirmwareData} disabled={isFilterDisabled}>
                            Filter
                        </AppButton>
                        <AppButton variant="ghost" size="medium" onClick={resetFilters}>
                            Reset
                        </AppButton>
                    </Box>
                </Box>
            </Paper>
            {isDeviceFirmwareDataLoading ? (
                <Box sx={{ display: "grid", placeItems: "center", height: "20vh" }}>
                    <CircularProgress size={48} sx={{ color: colors?.primary?.main || "#AFB42B" }} />
                </Box>
            ) : (
                showFirmwareDataList && (
                    <Paper sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 1 }}>
                        <DataTable
                            columns={tableColumns}
                            data={deviceFirmwareData}
                            rowKey="firmwareVersion"
                            minWidth="100%"
                            maxHeight={{ xs: "none", md: "58vh" }}
                            stickyHeader
                            striped={false}
                            hoverEffect={true}
                            tableContainerSx={{ boxShadow: "none", borderRadius: 0 }}
                            msgForDisplay="No Data found"
                        />
                    </Paper>
                )
            )}
        </Fragment>
    );
}

const UploadFirmware = ({
    allowedDeviceCategories,
    allowedCompanyBrand,
    firmwareData: { uploadDeviceFOTAFirmwareInProgress },
    uploadDeviceFOTAFirmware,
    uploadDeviceFactoryFirmwareWithAutoSign,
    intiFOTAFirmwareStatusInfo,
    setAlert,
}) => {
    const theme = useTheme();
    const defaultChipType = "ESPWIFI";

    const [uploadedCategoryName, setUploadedCategoryName] = useState("")
    const [uploadedBrandName, setUploadedBrandName] = useState("")
    const [modelNumbersList, setModelumbersList] = useState([])
    const [uploadedModelNumber, setUploadedModelNumber] = useState("")
    const [uploadedFirmwareVersion, setUploadedFirmwareVersion] = useState("")
    const [uploadedFirmwareImage, setUploadedFirmwareImage] = useState("")
    const [uploadedFirmwareSign, setUploadedFirmwareSign] = useState("")
    const [isMandatoryVersion, setIsMandatoryVersion] = useState(false)
    const [chipType, setChipType] = useState(defaultChipType)
    const [uploadMode, setUploadMode] = useState("with-signature")

    const firmwareImageInput = useRef()
    const firmwareSignInput = useRef()

    useEffect(() => {
        intiFOTAFirmwareStatusInfo();
    }, [intiFOTAFirmwareStatusInfo])

    useEffect(() => {
        if (!uploadedCategoryName || uploadedCategoryName.length===0) {
            return
        }
        if (!uploadedBrandName || uploadedBrandName.length===0) {
            return
        }

        if (uploadedBrandName && uploadedBrandName.length!==0 && uploadedCategoryName && uploadedCategoryName.length!==0) {
            getDeviceModelNumbersList(uploadedBrandName, uploadedCategoryName)
                .then((data) => {
                    setModelumbersList(data)
                    setUploadedModelNumber("")
                })
                .catch((err) => {
                    setModelumbersList([])
                    setUploadedModelNumber("")
                })
        }
    }, [uploadedBrandName, uploadedCategoryName])

    function handleDeviceUploadFirmwareReset() {
        setUploadedCategoryName("")
        setUploadedBrandName("")
        setUploadedModelNumber("")
        setUploadedFirmwareVersion("")
        setUploadedFirmwareImage("")
        setUploadedFirmwareSign("")
        setIsMandatoryVersion("false")
        setChipType(defaultChipType)
        setUploadMode("with-signature")

        firmwareImageInput.current.value = ""
        if (firmwareSignInput.current) {
            firmwareSignInput.current.value = ""
        }

        intiFOTAFirmwareStatusInfo() // reset the firmware states as well
    }

    function handleUploadDeviceFirmware() {
        if (!uploadedCategoryName || uploadedCategoryName.length===0) {
            setAlert('Category Name Cannot be Empty', 'danger')
            window.scrollTo(0, 0);
            return
        }
        if (!uploadedBrandName || uploadedBrandName.length===0) {
            setAlert('Brand Name Cannot be Empty', 'danger')
            window.scrollTo(0, 0);
            return
        }
        if (!uploadedFirmwareVersion || uploadedFirmwareVersion.length===0 || uploadedFirmwareVersion.match('^[0-9]{1,2}\\.[0-9]{1,2}$')===null) {
            setAlert('Invalid Firmware Version', 'danger')
            window.scrollTo(0, 0);
            return
        }
        if (!uploadedFirmwareImage || uploadedFirmwareImage==="" ) {
            setAlert('Invalid Firmware Image', 'danger')
            window.scrollTo(0, 0);
            return
        }
        if (!uploadedFirmwareImage.name.endsWith('.ota.bin')) {
            setAlert('Firmware Image should follow "*.ota.bin" name format.', 'danger')
            window.scrollTo(0, 0);
            return;
        }

        // With signature flow
        if (uploadMode === "with-signature") {
            if (!uploadedModelNumber || uploadedModelNumber.length===0) {
                setAlert('Model Number Cannot be Empty', 'danger')
                window.scrollTo(0, 0);
                return
            }
            if (!uploadedFirmwareSign || uploadedFirmwareSign==="" ) {
                setAlert('Invalid Signature File', 'danger')
                window.scrollTo(0, 0);
                return
            }
            if(!uploadedFirmwareSign.name.endsWith('.ota.bin.sig')) {
                setAlert('Firmware Signature should follow "*.ota.bin.sig" name format.', 'danger')
                window.scrollTo(0, 0);
                return;
            }

            let formData = new FormData()
            formData.append('files', uploadedFirmwareImage)
            formData.append('files', uploadedFirmwareSign)
            formData.append('signatureFileName', uploadedFirmwareSign.name)
            formData.append('modelNumber', uploadedModelNumber)
            formData.append('firmwareVersion', uploadedFirmwareVersion)
            formData.append('isMandatoryVersion', isMandatoryVersion)
            formData.append('chipType', chipType)

            uploadDeviceFOTAFirmware(formData)
            return
        }

        // Auto-sign flow
        if (!uploadedModelNumber || uploadedModelNumber.length===0) {
            setAlert('Model Number Cannot be Empty', 'danger')
            window.scrollTo(0, 0);
            return
        }

        let formData = new FormData()
        formData.append('files', uploadedFirmwareImage)
        formData.append('modelNumber', uploadedModelNumber)
        formData.append('firmwareVersion', uploadedFirmwareVersion)
        formData.append('chipType', chipType)
        formData.append('isMandatoryVersion', isMandatoryVersion)

        uploadDeviceFactoryFirmwareWithAutoSign(formData)
    };

    const chipTypeOptions =
        uploadedCategoryName === customConfig.deviceCategoryODMFRONTLOADWM
            ? customConfig.chipTypesByCategory.FLWM || []
            : uploadedCategoryName === customConfig.deviceCategoryLED
                ? customConfig.chipTypesByCategory.LED || []
                : customConfig.matterChipType || [];

    return (
        <Fragment>
            <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
                <Grid2 container spacing={2} sx={{ alignItems: "center" }}>
                    <Grid2 size={{ xs: 12, md: 2 }}><LabelText sx={{ fontWeight: 600 }}>Category:</LabelText></Grid2>
                    <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
                        <FormInput
                            type="select"
                            size="small"
                            fullWidth
                            label="Category"
                            value={uploadedCategoryName}
                            onChange={(e) => setUploadedCategoryName(e.target.value)}
                            options={(allowedDeviceCategories || []).map((c) => ({ value: c, label: c }))}
                            MenuProps={selectMenuProps}
                            sx={formControlSx(theme)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 2 }}><LabelText sx={{ fontWeight: 600 }}>Brand:</LabelText></Grid2>
                    <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
                        <FormInput
                            type="select"
                            size="small"
                            fullWidth
                            label="Brand"
                            value={uploadedBrandName}
                            onChange={(e) => setUploadedBrandName(e.target.value)}
                            options={(allowedCompanyBrand || []).map((b) => ({ value: b, label: b }))}
                            MenuProps={selectMenuProps}
                            sx={formControlSx(theme)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 2 }}><LabelText sx={{ fontWeight: 600 }}>Model Number:</LabelText></Grid2>
                    <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
                        <FormInput
                            type="select"
                            size="small"
                            fullWidth
                            label="Model Number"
                            value={uploadedModelNumber}
                            onChange={(e) => setUploadedModelNumber(e.target.value)}
                            options={(modelNumbersList || []).map((m) => ({ value: m, label: m }))}
                            MenuProps={selectMenuProps}
                            sx={formControlSx(theme)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 2 }}><LabelText sx={{ fontWeight: 600 }}>Chip Type:</LabelText></Grid2>
                    <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
                        <FormInput
                            type="select"
                            size="small"
                            fullWidth
                            label="Chip Type"
                            value={chipType}
                            onChange={(e) => setChipType(e.target.value)}
                            options={chipTypeOptions.map((t) => ({ value: t, label: t }))}
                            MenuProps={selectMenuProps}
                            sx={formControlSx(theme)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 2 }}><LabelText>Firmware Version:</LabelText></Grid2>
                    <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
                        <FormInput
                            type="text"
                            size="small"
                            fullWidth
                            label="Firmware Version"
                            value={uploadedFirmwareVersion}
                            onChange={(e) => setUploadedFirmwareVersion(e.target.value)}
                            sx={formControlSx(theme)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 2 }}>
                        <LabelText>Upload Mode:</LabelText>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 10 }}>
                        <AppRadioGroup
                            value={uploadMode}
                            onChange={(e) => setUploadMode(e.target.value)}
                            row={false}
                            formControlSx={{ ml: "-11px" }}
                            optionLabelSx={{ whiteSpace: "nowrap" }}
                            options={[
                                { value: "with-signature", label: "Upload firmware image & signature" },
                                { value: "auto-sign", label: "Upload firmware image" },
                            ]}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 2 }} />
                    <Grid2 size={{ xs: 12, md: 10 }}>
                        <AppCheckbox
                            checked={!!isMandatoryVersion}
                            onChange={(e) => setIsMandatoryVersion(e.target.checked)}
                            label="Mark as Mandatory Version"
                            color="default"
                            size="small"
                            sx={{ ml: "-11px" }}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 2 }}>
                        <LabelText sx={{ fontWeight: 600 }}>Firmware Image:</LabelText>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
                        <Box sx={formControlSx(theme)}>
                            <input
                                type="file"
                                accept=".ota.bin"
                                ref={firmwareImageInput}
                                onChange={(e) => setUploadedFirmwareImage(e.target.files?.[0] || "")}
                                style={{ width: "100%", boxSizing: "border-box", padding: 8, border: "1px solid rgba(0,0,0,0.42)", borderRadius: 4 }}
                            />
                        </Box>
                    </Grid2>
                    {uploadMode === "with-signature" && (
                        <>
                            <Grid2 size={{ xs: 12, md: 2 }}>
                                <LabelText sx={{ fontWeight: 600 }}>Firmware Signature:</LabelText>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
                                <Box sx={formControlSx(theme)}>
                                    <input
                                        type="file"
                                        accept=".ota.bin.sig"
                                        ref={firmwareSignInput}
                                        onChange={(e) => setUploadedFirmwareSign(e.target.files?.[0] || "")}
                                        style={{ width: "100%", boxSizing: "border-box", padding: 8, border: "1px solid rgba(0,0,0,0.42)", borderRadius: 4 }}
                                    />
                                </Box>
                            </Grid2>
                        </>
                    )}
                    <Grid2 size={12}>
                        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
                            <AppButton
                                variant="primary"
                                size="medium"
                                onClick={handleUploadDeviceFirmware}
                                disabled={!!uploadDeviceFOTAFirmwareInProgress}
                                loading={!!uploadDeviceFOTAFirmwareInProgress}
                            >
                                Upload
                            </AppButton>
                            <AppButton variant="ghost" size="medium" onClick={handleDeviceUploadFirmwareReset}>
                                Reset
                            </AppButton>
                        </Box>
                    </Grid2>
                </Grid2>
            </Paper>
        </Fragment>
    );
}

const mapStateToProps = (state) => ({
    allowedDeviceCategories: state.auth.allowedDeviceCategories,
    allowedCompanyBrand: state.auth.allowCompanyBrand,
    firmwareData: state.firmware
});

const mapDispatchToProps = (dispatch) => ({
    setAlert: (message, status) => dispatch(setAlert(message, status)),
    uploadDeviceFOTAFirmware: (formData) => dispatch(uploadDeviceFOTAFirmware(formData)),
    uploadDeviceFactoryFirmwareWithAutoSign: (formData) => dispatch(uploadDeviceFactoryFirmwareWithAutoSign(formData)),
    downloadDeviceFotaFirmwareFile: (params, fileType) => dispatch(downloadDeviceFotaFirmwareFile(params, fileType)),
    intiFOTAFirmwareStatusInfo: () => dispatch(intiFOTAFirmwareStatusInfo())
});

export default connect(mapStateToProps, mapDispatchToProps)(DeviceFOTAFirmware);

